import { z } from 'zod'

/** 저장 head 검증(3.3). `shipperCode`·`returnInvoiceNumber` 필수, `receiptStartedAt` 은 strict `\d{14}`. */
export const saveHeadSchema = z.object({
  shipperCode: z.string().min(1, '화주 정보가 없습니다.').max(100),
  returnInvoiceNumber: z.string().min(1, '반송장을 입력해주세요.').max(100),
  originalInvoiceNumber: z.string().max(100).optional(),
  receiverAddress: z.string().max(500).optional(),
  receiverPhone: z.string().max(30).optional(),
  receiverName: z.string().max(100).optional(),
  receiptStartedAt: z.string().regex(/^\d{14}$/, '스캔 시작 시각 형식이 올바르지 않습니다.'),
  jobCommentForInvoice: z.string().max(100).optional(),
  isSendToDoosonERP: z.boolean().optional(),
})

/** 저장 item 검증(3.3). 필수는 `orderNumber`·`orderQuantity`(양수) 뿐이다. */
export const saveItemSchema = z.object({
  returnId: z.number().positive().optional(),
  giId: z.number().positive().optional(),
  orderNumber: z.string().min(1).max(100),
  orderOptionNumber: z.string().max(35).optional(),
  plannedDate: z
    .string()
    .regex(/^\d{8}$/)
    .optional(),
  barcode: z.string().max(60).optional(),
  uid: z.string().max(70).optional(),
  skuId: z.string().max(14).optional(),
  optionCode: z.number().positive().optional(),
  goodsCode: z.string().max(100).optional(),
  goodsName: z.string().max(500).optional(),
  goodsOption: z.string().max(200).optional(),
  goodsColor: z.string().max(100).optional(),
  goodsGroup1: z.string().max(100).optional(),
  goodsAttr1: z.string().max(100).optional(),
  orderQuantity: z.number().positive('수량은 0보다 커야 합니다.'),
  condition: z.string().max(100).optional(),
  procurementName: z.string().max(2000).optional(),
  etc1: z.string().max(100).optional(),
  etc2: z.string().max(100).optional(),
  etc3: z.string().max(100).optional(),
  etc4: z.string().max(100).optional(),
  salesChannel: z.string().max(100).optional(),
  brandName: z.string().max(300).optional(),
  styleNumber: z.string().max(500).optional(),
  referenceNumber1: z.string().max(100).optional(),
  referenceNumber2: z.string().max(100).optional(),
  referenceNumber5: z.string().max(100).optional(),
  logisticsProviderName: z.string().max(100).optional(),
  transferType: z.string().max(200).optional(),
  transferNumber: z.string().max(100).optional(),
  transferMessage: z.string().max(1000).optional(),
  warehouseName: z.string().max(100).optional(),
  warehouseArea: z.string().max(20).optional(),
  orderOptionSequence: z.number().int().min(0).optional(),
  purchasePrice: z.number().min(0).optional(),
  lotAttribute5: z.string().max(100).optional(),
  claimId: z.string().max(20).optional(),
  claimType: z.string().max(100).optional(),
  incoterms: z.string().max(100).optional(),
  orderStatus: z.string().max(100).optional(),
})

/** 저장 요청 전체 — items 는 최소 1건·최대 1000건(3.3). */
export const saveRequestSchema = z.object({
  head: saveHeadSchema,
  items: z.array(saveItemSchema).min(1, '검수한 상품이 없습니다.').max(1000, '한 번에 저장할 수 있는 상품은 1000건까지입니다.'),
})

export type SaveHeadSchemaType = z.infer<typeof saveHeadSchema>
export type SaveItemSchemaType = z.infer<typeof saveItemSchema>
export type SaveRequestSchemaType = z.infer<typeof saveRequestSchema>

export interface ValidationResult {
  success: boolean
  message?: string
  /** 특정 행(`items[N]`)을 짚는 에러면 true — 행 짚는 검증 안내(scanNotice.rowInvalid — 스캔 화면은 큰 토스트). */
  isRowIssue?: boolean
}

/**
 * 저장 직전 검증 — 첫 에러 메시지만 화면에 안내한다(6절 5단계). `items[N]` 에서 난 에러는 몇 번째
 * 행인지 알 수 없으면 사용자가 어디를 고쳐야 할지 못 찾으므로 행 번호(1-base)를 앞에 붙인다
 * (PR #691 리뷰 코멘트 — `orderQuantity` 가 `null → 0` 으로 떨어져 검증에 걸리는 행을 특정하기 어렵다는 지적).
 */
export const validateSaveRequest = (req: unknown): ValidationResult => {
  const result = saveRequestSchema.safeParse(req)
  if (result.success) return { success: true }
  const issue = result.error.issues[0]
  if (!issue) return { success: false, message: '저장 정보를 확인해 주세요.' }
  const [section, rowIndex] = issue.path
  if (section === 'items' && typeof rowIndex === 'number') {
    return { success: false, message: `${rowIndex + 1}번째 행 — ${issue.message}`, isRowIssue: true }
  }
  return { success: false, message: issue.message }
}
